import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Users, Globe, Palette, CreditCard, Settings, ScrollText,
  PlusCircle, Receipt, Package, ShoppingCart, ShoppingBag, TicketPercent, Moon, Sun,
  Bell, LogOut, Menu, X, Zap, Store, Paintbrush, KeyRound, ShieldAlert, Percent, UserRound, Scale, Smartphone, FolderTree, Upload, TrendingUp, Target, Share2, Rocket, Sparkles, Bot, Megaphone, Trophy, Briefcase, MessageSquare, GraduationCap, Video, PlayCircle, BadgePercent, IndianRupee, Clapperboard, Link2, BarChart3, ArrowDownToLine, LifeBuoy, BadgeIndianRupee, Building2, Gift,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import RoleBadge from "./RoleBadge";
import BrandLogo from "./BrandLogo";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import PresetSwitcher, { applyPreset, initPreset } from "../components/PresetSwitcher";

const NAV = {
  ROOT_SUPER_ADMIN: [
    { to: "/root", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/root/admins", label: "Super Admins", icon: Users },
    { to: "/root/access", label: "Account Access", icon: KeyRound },
    { to: "/root/plans", label: "Admin Plans", icon: CreditCard },
    { to: "/root/revenue", label: "Revenue Share", icon: Percent },
    { to: "/root/self-service", label: "Self-Service Sales", icon: Rocket },
    { to: "/root/crm", label: "CRM Revenue", icon: Target },
    { to: "/root/ai", label: "AI Credits & Revenue", icon: Bot },
    { to: "/root/challenges", label: "Challenges & Leaderboard", icon: Trophy },
    { to: "/root/marketplace", label: "Marketplace", icon: ShoppingBag },
    { to: "/root/creators", label: "Videos & Creators", icon: Video },
    { to: "/root/ads", label: "Advertisements", icon: Megaphone },
    { to: "/root/site-affiliate", label: "Site Affiliate", icon: Store },
    { to: "/root/app-branding", label: "App & PWA", icon: Smartphone },
    { to: "/root/app-analytics", label: "App Analytics", icon: BarChart3 },
    { to: "/root/realestate", label: "Real Estate", icon: Building2 },
    { to: "/root/legal", label: "Legal Pages", icon: Scale },
    { to: "/root/domains", label: "Custom Domains", icon: Globe },
    { to: "/root/import", label: "Import Theme", icon: Upload },
    { to: "/root/seo", label: "SEO & Marketing", icon: TrendingUp },
    { to: "/root/partners", label: "All Partners", icon: Globe },
    { to: "/root/websites", label: "All Websites", icon: Store },
    { to: "/root/payments", label: "Payments", icon: Receipt },
    { to: "/root/audit", label: "Audit Logs", icon: ScrollText },
  ],
  SUPER_ADMIN: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/partners", label: "Partners", icon: Users },
    { to: "/admin/websites", label: "Websites", icon: Globe },
    { to: "/admin/themes", label: "Themes", icon: Palette },
    { to: "/admin/payments", label: "Payments", icon: CreditCard },
    { to: "/admin/settings", label: "Settings", icon: Settings },
    { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
  ],
  PARTNER: [
    { to: "/partner", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/partner/websites", label: "My Websites", icon: Globe },
    { to: "/partner/websites/new", label: "Create Website", icon: PlusCircle },
    { to: "/partner/referrals", label: "Referrals & Earnings", icon: Share2 },
    { to: "/partner/leaderboard", label: "Top Freelancers", icon: Trophy },
    { to: "/partner/challenges", label: "Earning Challenges", icon: Trophy },
    { to: "/freelancer", label: "Freelancer Studio", icon: Briefcase },
    { to: "/marketplace", label: "Marketplace", icon: Store },
    { to: "/mkt/orders", label: "My Purchases", icon: ShoppingBag },
    { to: "/mkt/messages", label: "Messages", icon: MessageSquare },
    { to: "/freelancer/affiliate", label: "Affiliate Program", icon: BadgePercent },
    { to: "/partner/branding", label: "Branding", icon: Paintbrush },
    { to: "/partner/billing", label: "Billing", icon: Receipt },
  ],
  BUSINESS_OWNER: [
    { to: "/store", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/store/domain", label: "Custom Domain", icon: Globe },
    { to: "/store/editor", label: "Visual Editor", icon: Paintbrush },
    { to: "/store/products", label: "Products", icon: Package },
    { to: "/store/categories", label: "Categories", icon: FolderTree },
    { to: "/store/orders", label: "Orders", icon: ShoppingCart },
    { to: "/store/customers", label: "Customers", icon: Users },
    { to: "/store/crm", label: "CRM", icon: Target },
    { to: "/store/realestate", label: "Real Estate", icon: Building2 },
    { to: "/store/re-org", label: "RE Org Panel", icon: Users },
    { to: "/store/re-editor", label: "RE Site Editor", icon: Paintbrush },
    { to: "/store/ai-studio", label: "AI Design Studio", icon: Sparkles },
    { to: "/store/ai", label: "Growth AI", icon: Megaphone },
    { to: "/store/coupons", label: "Coupons", icon: TicketPercent },
    { to: "/store/settings", label: "Settings", icon: Settings },
    { to: "/store/seo", label: "SEO", icon: TrendingUp },
    { to: "/store/app", label: "App / PWA", icon: Smartphone },
    { to: "/freelancer", label: "Freelancer Studio", icon: Briefcase },
    { to: "/marketplace", label: "Marketplace", icon: Store },
    { to: "/mkt/orders", label: "My Purchases", icon: ShoppingBag },
    { to: "/mkt/messages", label: "Messages", icon: MessageSquare },
    { to: "/store/affiliate", label: "Affiliate Program", icon: BadgePercent },
    { to: "/store/account", label: "Account", icon: UserRound },
  ],
  CUSTOMER: [
    { to: "/mkt/account", label: "My Account", icon: UserRound, end: true },
    { to: "/marketplace", label: "Marketplace", icon: Store },
    { to: "/mkt/orders", label: "My Orders", icon: ShoppingBag },
    { to: "/mkt/courses", label: "My Courses", icon: GraduationCap },
    { to: "/mkt/messages", label: "Messages", icon: MessageSquare },
    { to: "/mkt/offers", label: "Custom Offers", icon: Percent },
    { to: "/freelancer", label: "Freelancer Studio", icon: Briefcase },
  ],
  CREATOR: [
    { to: "/creator", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/creator/videos/new", label: "Add Video", icon: PlusCircle },
    { to: "/creator/videos", label: "My Videos", icon: PlayCircle, end: true },
    { to: "/creator/offers", label: "Affiliate Products", icon: BadgePercent },
    { to: "/creator/links", label: "Affiliate Links", icon: Link2 },
    { to: "/creator/monetization", label: "Monetization", icon: BadgeIndianRupee },
    { to: "/creator/referrals", label: "Refer & Earn", icon: Gift },
    { to: "/creator/site-affiliate", label: "Site Affiliate", icon: Share2 },
    { to: "/creator/earnings", label: "Earnings", icon: IndianRupee, end: true },
    { to: "/creator/withdrawals", label: "Withdrawals", icon: ArrowDownToLine },
    { to: "/creator/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/creator/profile", label: "Profile", icon: UserRound },
    { to: "/creator/settings", label: "Settings", icon: Settings },
    { to: "/creator/support", label: "Support", icon: LifeBuoy },
    { to: "/videos", label: "MILTROO Videos", icon: Clapperboard },
  ],
};

const ROLE_LABEL = { ROOT_SUPER_ADMIN: "Root Admin", SUPER_ADMIN: "Super Admin", PARTNER: "Partner", BUSINESS_OWNER: "Store Admin", CUSTOMER: "Member", CREATOR: "Creator" };

function applyTheme(mode) {
  document.documentElement.classList.toggle("dark", mode === "dark");
  localStorage.setItem("zylo_theme", mode);
}

function NotificationsBell() {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative h-10 w-10 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full gradient-bg text-white text-[10px] font-bold flex items-center justify-center glow-sm">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-display font-bold text-sm">Notifications</span>
          <button data-testid="notifications-mark-read" onClick={async () => { await api.patch("/notifications/read-all"); load(); }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors">Mark all read</button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.notifications.length === 0 && <p className="text-sm text-muted-foreground p-6 text-center">No notifications yet</p>}
          {data.notifications.map((n) => (
            <div key={n.id} data-testid={`notification-${n.id}`} data-notif-type={n.type || "general"} className={`px-4 py-3 border-b border-border last:border-0 ${n.read ? "opacity-60" : ""}`}>
              <p className="text-sm font-semibold">{n.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout() {
  const { user, logout, impersonator, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem("zylo_theme") || (user?.role === "BUSINESS_OWNER" ? "light" : "dark"));
  const [preset, setPreset] = useState(initPreset);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { applyTheme(theme); }, [theme]);
  useEffect(() => {
    if (!localStorage.getItem("zylo_theme") && user) {
      const def = user.role === "BUSINESS_OWNER" ? "light" : "dark";
      setTheme(def); applyTheme(def);
    }
  }, [user]);

  if (!user) return null;
  const items = NAV[user.role] || [];

  const NavItems = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onClick} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
              isActive ? "gradient-bg text-white glow-sm" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
          <Icon className="h-4 w-4" /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-card z-40">
        <div className="flex items-center px-6 h-16 border-b border-border">
          <BrandLogo size="md" />
        </div>
        <div className="flex-1 overflow-y-auto p-4"><NavItems /></div>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 px-2 mb-3">
            {user.avatar ? (
              <img src={fileUrl(user.avatar)} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" data-testid="sidebar-avatar" />
            ) : (
              <div className="h-9 w-9 rounded-full gradient-bg text-white flex items-center justify-center font-display font-bold text-sm shrink-0">
                {user.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
          <RoleBadge />
        </div>
      </aside>

      <header className="glass-header sticky top-0 z-30 lg:pl-64">
        <div className="flex items-center gap-3 h-16 px-4 sm:px-8">
          <button data-testid="mobile-menu-button" className="lg:hidden h-10 w-10 rounded-full border border-border flex items-center justify-center" onClick={() => setMobileOpen(true)}>
            <Menu className="h-4 w-4" />
          </button>
          <div className="lg:hidden flex items-center gap-2">
            <BrandLogo size="sm" />
          </div>
          {impersonator && (
            <div data-testid="impersonation-indicator" className="flex items-center gap-2.5 rounded-full border border-amber-500/50 bg-amber-500/10 pl-3 pr-1.5 py-1.5">
              <ShieldAlert className="h-4 w-4 text-amber-500 shrink-0" />
              <span className="text-xs font-bold hidden md:inline">Viewing as {user.name} · {ROLE_LABEL[user.role]}</span>
              <button data-testid="return-to-root-button" onClick={() => { exitImpersonation(); navigate("/root"); }}
                className="px-3 py-1 rounded-full text-[11px] font-extrabold bg-amber-500 text-black hover:bg-amber-400 transition-colors whitespace-nowrap">
                Return to Root Admin
              </button>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {user.role === "BUSINESS_OWNER" && (
              <Button variant="outline" size="sm" className="rounded-full hidden sm:flex" data-testid="view-storefront-button"
                onClick={async () => { const { data } = await api.get("/store-admin/settings"); window.open(`/site/${data.slug}`, "_blank"); }}>
                <Store className="h-3.5 w-3.5 mr-1.5" /> View Store
              </Button>
            )}
            <NotificationsBell />
            <PresetSwitcher value={preset} onChange={(p) => { setPreset(p); applyPreset(p); }} />
            <button data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="h-10 w-10 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="h-10 w-10 rounded-full gradient-bg text-white flex items-center justify-center font-display font-bold hover:opacity-85 transition-opacity glow-sm">
                  {user.name?.[0]?.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 inset-y-0 w-72 bg-card border-r border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <BrandLogo size="md" />
              <button onClick={() => setMobileOpen(false)} className="h-9 w-9 rounded-full border border-border flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <NavItems onClick={() => setMobileOpen(false)} />
            <div className="mt-6"><RoleBadge /></div>
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <motion.div key={location.pathname} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}
